import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MapPinned } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { NumericKeypad } from "../components/NumericKeypad";

const MATRICULE_LENGTH = 7;
const PIN_LENGTH = 8;

const C = { bg: "#12151a", panel: "#1a1e25", line: "#2b313b", amber: "#e8a33d", text: "#f0ede4", muted: "#8b909b" };

export default function Login() {
  const [step, setStep] = useState("matricule");
  const [matricule, setMatricule] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) navigate(location.state?.from || "/", { replace: true });
  }, [user, navigate, location.state]);

  const length = step === "matricule" ? MATRICULE_LENGTH : PIN_LENGTH;
  const value = step === "matricule" ? matricule : pin;

  // Valeurs courantes en ref : une frappe clavier rapide (plusieurs touches
  // avant le prochain rendu) ne doit perdre aucun chiffre.
  const st = useRef({ step, matricule, pin, loading });
  st.current = { step, matricule, pin, loading };

  // Matricule seul pour les utilisateurs ; le serveur répond « pin_requis »
  // pour un administrateur, et l'écran demande alors le code.
  const submit = useCallback(async (mat, fullPin) => {
    setLoading(true);
    setError("");
    try {
      await login(mat, fullPin || undefined);
      navigate(location.state?.from || "/", { replace: true });
    } catch (e) {
      if (!fullPin && e?.response?.data?.pin_requis) {
        st.current = { ...st.current, step: "pin", pin: "", loading: false };
        setStep("pin");
        setPin("");
      } else {
        setError(errMsg(e) || "Identifiant ou code incorrect");
        setPin("");
        if (!fullPin) { st.current = { ...st.current, matricule: "", step: "matricule" }; setMatricule(""); }
      }
    } finally {
      setLoading(false);
    }
  }, [login, navigate, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const press = useCallback((digit) => {
    const s = st.current;
    if (s.loading) return;
    if (s.step === "matricule") {
      const next = (s.matricule + digit).slice(0, MATRICULE_LENGTH);
      st.current = { ...s, matricule: next };
      setMatricule(next);
      setError("");
      if (next.length === MATRICULE_LENGTH) {
        st.current.loading = true;
        submit(next, null);
      }
      return;
    }
    const next = (s.pin + digit).slice(0, PIN_LENGTH);
    st.current = { ...s, pin: next };
    setPin(next);
    if (next.length === PIN_LENGTH) {
      st.current.loading = true;
      submit(s.matricule, next);
    }
  }, [submit]);

  const backspace = useCallback(() => {
    const s = st.current;
    if (s.step === "matricule") setMatricule((p) => p.slice(0, -1));
    else if (s.pin.length === 0) setStep("matricule");
    else setPin((p) => p.slice(0, -1));
  }, []);

  const ok = useCallback(() => {
    const s = st.current;
    if (s.step === "matricule" && s.matricule.length === MATRICULE_LENGTH) submit(s.matricule, null);
    else if (s.step === "pin" && s.pin.length === PIN_LENGTH) submit(s.matricule, s.pin);
  }, [submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (/^\d$/.test(e.key)) { e.preventDefault(); press(e.key); }
      else if (e.key === "Backspace") { e.preventDefault(); backspace(); }
      else if (e.key === "Enter") { e.preventDefault(); ok(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press, backspace, ok]);

  const keyCls = "min-h-[60px] rounded-xl text-xl font-semibold active:scale-95 transition-transform duration-100 disabled:opacity-50 border";

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: C.bg, color: C.text }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: C.amber, color: C.bg }}>
            <MapPinned size={22} />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tracking-tight">Carte des tournées</p>
            <p className="text-xs" style={{ color: C.muted }}>Livraison — secteurs Occitanie</p>
          </div>
        </div>

        <div className="rounded-2xl p-6 border" style={{ background: C.panel, borderColor: C.line }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium" data-testid="login-etape">{step === "matricule" ? "Matricule" : "Code administrateur"}</p>
            {step === "pin" && (
              <button
                type="button"
                className="text-xs flex items-center gap-1 hover:underline"
                style={{ color: C.muted }}
                onClick={() => { setStep("matricule"); setPin(""); setError(""); }}
              >
                <ArrowLeft size={12} /> {matricule}
              </button>
            )}
          </div>
          <p className="text-xs mb-5" style={{ color: C.muted }}>
            {step === "matricule" ? `${MATRICULE_LENGTH} chiffres` : `${PIN_LENGTH} chiffres — compte administrateur`}
          </p>

          <div className="flex justify-center gap-2 mb-5" data-testid="login-dots">
            {Array.from({ length }).map((_, i) => (
              <span
                key={i}
                className="w-3 h-3 rounded-full border transition-colors"
                style={{
                  borderColor: i < value.length ? C.amber : C.line,
                  background: i < value.length ? C.amber : "transparent",
                }}
              />
            ))}
          </div>

          <div className="min-h-[20px] mb-3 text-center text-sm" style={{ color: "#f0806f" }} data-testid="login-error">
            {loading ? <Loader2 className="inline animate-spin" size={16} style={{ color: C.amber }} /> : error}
          </div>

          <NumericKeypad
            onDigit={press}
            onBackspace={backspace}
            onSubmit={ok}
            disabled={loading}
            showSubmit
            testIdPrefix="login"
            keyClassName={`${keyCls} bg-[#20252d] border-[#2b313b] hover:border-[#e8a33d] text-[#f0ede4]`}
          />
        </div>
        <p className="text-center text-xs mt-4" style={{ color: C.muted }}>Le clavier physique fonctionne aussi.</p>
      </div>
    </div>
  );
}
